import { useEffect, useRef, useState } from "react";
import { Trash2, Upload } from "lucide-react";

import {
  useDeleteLogo,
  useOrganisation,
  usePatchOrganisation,
  useUploadLogo,
} from "@/api/organisation-api";
import { useToast } from "@/hooks/use-toast";
import { APP_NAME } from "@/lib/branding";

/**
 * The practice's own name and mark.
 *
 * WHY THIS SCREEN EXISTS. The API for it has been here since the
 * extraction — `PATCH /api/organisation`, `POST /api/organisation/logo`
 * — with nothing in the console calling either. So the one piece of
 * branding a candidate actually sees, on the login page before they have
 * signed in, could only be changed with a hand-written HTTP request.
 *
 * THE DISTINCTION THIS SCREEN HAS TO MAKE CLEAR. Two names appear on the
 * login page and they belong to different people:
 *
 *   - the PRACTICE's name and logo, set here, per deployment;
 *   - the PRODUCT's name ({APP_NAME}), which is built into the bundle.
 *
 * Confusing them is the obvious mistake — somebody types their practice
 * name here expecting the product to be renamed, or goes looking for a
 * setting to change the product name and does not find one. Both are
 * said plainly on screen rather than left to be discovered.
 */
export function OrganisationPanel() {
  const { toast } = useToast();
  const org = useOrganisation();
  const patch = usePatchOrganisation();
  const upload = useUploadLogo();
  const remove = useDeleteLogo();

  const [name, setName] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);

  // Seeded from the server once it answers. Not a `key` remount, so a
  // half-typed name is never thrown away by a background refetch.
  const loaded = useRef(false);
  useEffect(() => {
    if (!loaded.current && org.data) {
      loaded.current = true;
      setName(org.data.name ?? "");
    }
  }, [org.data]);

  const logoUrl = org.data?.primary_logo_url ?? null;
  const dirty = !!org.data && name.trim() !== (org.data.name ?? "").trim();
  const canSave = dirty && name.trim().length > 0 && !patch.isPending;

  const save = async () => {
    if (!canSave) return;
    try {
      await patch.mutateAsync({ name: name.trim() });
      toast({ title: "Practice name saved" });
    } catch {
      toast({ title: "Could not save the name", variant: "error" });
    }
  };

  const pickFile = () => fileRef.current?.click();

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      await upload.mutateAsync({
        type: "primary",
        file,
        // Sent explicitly rather than left to the Blob: the server uses
        // the filename only for its extension check, and a Blob with no
        // name reaches it as "blob", which fails that check with a
        // message about the file type rather than the missing name.
        filename: file.name,
      });
      toast({ title: "Logo updated" });
    } catch (err) {
      // The server checks the type and size, and sanitises an SVG before
      // it is ever served — an SVG is a document that can carry script,
      // and this one goes to every browser that reaches the login page.
      // So its refusal is the useful message, not a generic one.
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? "Could not upload that file";
      toast({ title: detail, variant: "error" });
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const clearLogo = async () => {
    try {
      await remove.mutateAsync("primary");
      toast({ title: "Logo removed" });
    } catch {
      toast({ title: "Could not remove the logo", variant: "error" });
    }
  };

  if (org.isLoading) {
    return (
      <p className="text-sm text-foreground-muted">Loading organisation…</p>
    );
  }

  return (
    <div className="max-w-xl" data-cy="organisation-panel">
      <section className="rounded-xl border border-border bg-background-card p-5">
        <h2 className="text-base font-semibold text-foreground">
          Practice name
        </h2>
        <p className="mt-1 text-xs text-foreground-muted">
          Shown above the sign-in box, and in the emails candidates
          receive. This is the practice — not the product, which is called{" "}
          <span className="font-medium text-foreground">{APP_NAME}</span> and
          is set at build time.
        </p>

        <label className="sk-field mt-4 flex flex-col">
          <span className="sk-label">Name</span>
          <input
            data-cy="org-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={255}
            placeholder="Northgate Radiology"
            className="sk-input mt-1 w-auto"
          />
        </label>

        <button
          type="button"
          data-cy="btn-save-org"
          onClick={save}
          disabled={!canSave}
          className="sk-btn sk-btn-primary mt-4"
        >
          {patch.isPending ? "Saving…" : "Save name"}
        </button>
      </section>

      <section className="mt-4 rounded-xl border border-border bg-background-card p-5">
        <h2 className="text-base font-semibold text-foreground">Logo</h2>
        <p className="mt-1 text-xs text-foreground-muted">
          Appears on the sign-in page, which a candidate sees before they
          have an account context — so it is the first thing that tells
          them they are in the right place. PNG, JPEG or SVG.
        </p>

        <div className="mt-4 flex items-center gap-4">
          <div className="flex h-20 w-40 shrink-0 items-center justify-center rounded-md border border-dashed border-border bg-background p-2">
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Current practice logo"
                data-cy="org-logo-preview"
                className="max-h-full max-w-full object-contain"
              />
            ) : (
              <span className="text-[11px] text-foreground-muted">
                No logo yet
              </span>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/svg+xml"
              data-cy="org-logo-input"
              onChange={(e) => void onFile(e.target.files?.[0])}
              className="hidden"
            />
            <button
              type="button"
              data-cy="btn-upload-logo"
              onClick={pickFile}
              disabled={upload.isPending}
              className="sk-btn sk-btn-primary sk-sm"
            >
              <Upload className="h-3.5 w-3.5" />
              {upload.isPending
                ? "Uploading…"
                : logoUrl
                  ? "Replace logo"
                  : "Upload logo"}
            </button>
            {logoUrl && (
              <button
                type="button"
                data-cy="btn-remove-logo"
                onClick={clearLogo}
                disabled={remove.isPending}
                className="flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-xs text-foreground-muted hover:text-foreground disabled:opacity-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            )}
          </div>
        </div>

        <p className="mt-3 text-[11px] text-foreground-muted">
          An uploaded SVG is sanitised before it is stored — an SVG is a
          document that can carry script, and this one is served to every
          browser that reaches the sign-in page.
        </p>
      </section>
    </div>
  );
}
